import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Check, Loader2, Upload, AlertCircle, ShieldCheck,
  PartyPopper, Mail, X,
} from "lucide-react";
import { LogoMark } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ------------------------- option sets ------------------------- */
const PLATFORMS = [
  "DoorDash","Uber","Lyft","Instacart","Spark",
  "Roadie","Other",
];
const GIG_EXP = [
  ["lt-3-months",  "Less than 3 months"],
  ["3-12-months",  "3–12 months"],
  ["1-3-years",    "1–3 years"],
  ["3+-years",     "3+ years"],
];
const WEEKLY_EARN = [
  ["under-250",   "Under $250"],
  ["250-500",     "$250–$500"],
  ["500-750",     "$500–$750"],
  ["750-1000",    "$750–$1,000"],
  ["1000-1500",   "$1,000–$1,500"],
  ["1500+",       "$1,500+"],
];
const DAILY_EARN = [
  ["under-50",  "Under $50"],
  ["50-100",    "$50–$100"],
  ["100-150",   "$100–$150"],
  ["150-250",   "$150–$250"],
  ["250+",      "$250+"],
];
const DAYS_WEEK = [
  ["1-2", "1–2 days"], ["3-4","3–4 days"], ["5-6","5–6 days"], ["7","7 days"],
];
const NEXT_PAYOUT = [
  ["today","Today"], ["1-2-days","1–2 days"], ["3-4-days","3–4 days"],
  ["5-6-days","5–6 days"], ["7+-days","7+ days"], ["not-sure","Not sure"],
];
const USE_CASES = [
  "Gas","Tire","Oil change","Vehicle repair","Food","Childcare",
  "Utility bill","Phone bill","Emergency expense","Other",
];
const PAY_METHODS = [
  ["Cash App","Cash App"], ["Zelle","Zelle"],
];
const YESNO = [["yes","Yes"],["no","No"]];
const YESNOMAYBE = [["yes","Yes"],["no","No"],["maybe","Maybe"]];
const ACQ_SOURCES = [
  "TikTok","Instagram","Facebook","Reddit","Friend / referral",
  "Gig worker group","Google","Other",
];

/* ------------------------- UI primitives ------------------------- */
const Field = ({ label, hint, children, required = true }) => (
  <label className="block mb-5">
    <span className="block font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">
      {label}
      {required && <span className="ml-1 text-[#00FF88]">*</span>}
    </span>
    {children}
    {hint && <span className="block mt-1.5 text-[11px] text-white/35 leading-relaxed">{hint}</span>}
  </label>
);

const Input = (props) => (
  <input
    {...props}
    className={`w-full h-12 px-4 bg-[#0A0A0A] border border-white/10 rounded-2xl text-white placeholder:text-white/30 focus:outline-none focus:border-[#00FF88]/50 focus:ring-2 focus:ring-[#00FF88]/20 transition-all ${props.className || ""}`}
  />
);

const OptionButton = ({ active, children, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`w-full text-left px-4 py-3 rounded-2xl border transition-all flex items-center justify-between gap-3
      ${active
        ? "bg-[#00FF88]/10 border-[#00FF88]/50 text-white shadow-[0_0_0_3px_rgba(0,255,136,0.08)]"
        : "bg-[#0A0A0A] border-white/8 text-white/80 hover:border-white/20 hover:bg-white/[0.02]"}`}
  >
    <span className="flex-1 text-sm">{children}</span>
    {active && (
      <span className="w-5 h-5 rounded-full bg-[#00FF88] grid place-items-center shrink-0">
        <Check className="w-3 h-3 text-black" />
      </span>
    )}
  </button>
);

const Pill = ({ active, children, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all
      ${active
        ? "bg-[#00FF88] text-black border-[#00FF88]"
        : "bg-[#0A0A0A] text-white/75 border-white/10 hover:border-white/30"}`}
  >
    {children}
  </button>
);

const FileDrop = ({ label, file, onChange, hint, required, testid }) => (
  <label className="block mb-5">
    <span className="block font-mono-c text-[10px] uppercase tracking-[0.2em] text-white/50 mb-2">
      {label}
      {required && <span className="ml-1 text-[#00FF88]">*</span>}
    </span>
    <div className={`relative rounded-2xl border-2 border-dashed px-4 py-5 transition-all ${
      file ? "border-[#00FF88]/40 bg-[#00FF88]/5" : "border-white/10 bg-[#0A0A0A] hover:border-white/25"
    }`}>
      <input
        type="file"
        accept="image/jpeg,image/png,image/jpg,application/pdf,image/webp"
        onChange={(e) => onChange(e.target.files?.[0] || null)}
        className="absolute inset-0 opacity-0 cursor-pointer"
        data-testid={testid}
      />
      <div className="flex items-center gap-3 pointer-events-none">
        <span className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${file ? "bg-[#00FF88]/15 text-[#00FF88]" : "bg-white/5 text-white/40"}`}>
          {file ? <Check className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
        </span>
        <div className="min-w-0 flex-1">
          {file ? (
            <>
              <div className="text-sm text-white font-semibold truncate">{file.name}</div>
              <div className="text-[11px] text-white/45 font-mono-c">{Math.round(file.size / 1024)} KB</div>
            </>
          ) : (
            <>
              <div className="text-sm text-white/80 font-semibold">Tap to upload (JPG, PNG, PDF)</div>
              <div className="text-[11px] text-white/40 font-mono-c">Max 10 MB · From phone gallery is fine</div>
            </>
          )}
        </div>
      </div>
    </div>
    {hint && <span className="block mt-2 text-[11px] text-white/40 leading-relaxed">{hint}</span>}
  </label>
);

const Checkbox = ({ checked, onChange, children, testid }) => (
  <label className="flex items-start gap-3 cursor-pointer py-2">
    <input
      type="checkbox"
      checked={!!checked}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-1 w-4 h-4 accent-[#00FF88] cursor-pointer shrink-0"
      data-testid={testid}
    />
    <span className="text-[13px] text-white/75 leading-relaxed">{children}</span>
  </label>
);

/* ------------------------- helpers ------------------------- */
const todayISO = () => new Date().toISOString().slice(0, 10);
const REPAYMENT_DAYS = { "25": 3, "50": 5 };
const repaymentDaysFor = (amt) => REPAYMENT_DAYS[String(amt)] || 5;
const dueDateFromAmount = (amt) => {
  const d = new Date();
  d.setDate(d.getDate() + repaymentDaysFor(amt));
  return d.toISOString().slice(0, 10);
};
const ageFromDob = (dob) => {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d)) return null;
  const today = new Date();
  let a = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) a--;
  return a;
};

const initialForm = () => ({
  first_name: "", last_name: "", email: "", phone: "",
  city: "", state: "", zip_code: "", date_of_birth: "",
  currently_gig_working: "",
  platforms: [], primary_platform: "",
  gig_experience: "",
  weekly_earnings_range: "", daily_earnings_range: "",
  days_per_week: "", next_payout: "",
  amount_requested: "",
  use_cases: [], use_explanation: "",
  preferred_repayment_date: "",
  payment_method: "",
  payment_handle: "", payment_handle_confirm: "",
  payment_screenshot: null,
  current_earnings_screenshot: null,
  previous_earnings_screenshot: null,
  additional_earnings_screenshot: null,
  gig_name_match: "", payment_name_match: "",
  name_mismatch_explanation: "",
  consent_pilot_program: false, consent_repay_terms: false,
  consent_no_interest: false, consent_manual_repayment: false,
  consent_accurate_info: false, consent_no_guarantee: false,
  consent_contact: false, consent_honest_participation: false,
  followup_survey: "", founder_interview: "",
  testimonial_willing: "", beta_testing: "",
  acquisition_source: "", referral_handle: "",
});

/* ------------------------- main component ------------------------- */
const Pilot = () => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const togglePlatform = (p) => {
    const cur = form.platforms;
    const next = cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p];
    const patch = { platforms: next };
    if (!form.primary_platform && next.length === 1) patch.primary_platform = next[0];
    if (form.primary_platform && !next.includes(form.primary_platform)) patch.primary_platform = next[0] || "";
    set(patch);
  };

  const toggleUse = (u) => {
    const cur = form.use_cases;
    const next = cur.includes(u) ? cur.filter((x) => x !== u) : [...cur, u];
    set({ use_cases: next });
  };

  const age = useMemo(() => ageFromDob(form.date_of_birth), [form.date_of_birth]);
  const nameMismatch = form.gig_name_match === "no" || form.payment_name_match === "no";

  /* Compute a human-readable list of which required fields are still
   * missing so customers see EXACTLY what's blocking submission instead
   * of staring at a greyed-out button.
   */
  const missingFields = useMemo(() => {
    const m = [];
    if (form.first_name.trim().length < 1) m.push("First name");
    if (form.last_name.trim().length < 1) m.push("Last name");
    if (!/^\S+@\S+\.\S+$/.test(form.email)) m.push("Valid email address");
    if (form.phone.replace(/\D/g, "").length < 10) m.push("Phone (10+ digits)");
    if (form.city.trim().length < 1) m.push("City");
    if (!/^[A-Z]{2}$/.test(form.state.trim())) m.push("State (2 letters, uppercase — e.g. CA)");
    if (!/^\d{5}(-\d{4})?$/.test(form.zip_code.trim())) m.push("ZIP (12345 or 12345-6789)");
    if (!form.date_of_birth) m.push("Date of birth");
    else if (age !== null && age < 18) m.push("Must be 18 or older");
    if (!form.currently_gig_working) m.push("Currently gig working (Yes/No)");
    if (form.platforms.length < 1) m.push("At least 1 gig platform");
    if (!form.primary_platform) m.push("Primary gig platform");
    if (!form.gig_experience) m.push("Gig experience");
    if (!form.weekly_earnings_range) m.push("Weekly earnings range");
    if (!form.daily_earnings_range) m.push("Daily earnings range");
    if (!form.days_per_week) m.push("Days per week working");
    if (!form.next_payout) m.push("Next payout date");
    if (!(form.amount_requested === "25" || form.amount_requested === "50")) m.push("Float amount ($25 or $50)");
    if (form.use_cases.length < 1) m.push("At least 1 use case");
    if (form.use_explanation.trim().length < 5) m.push("Use explanation (5+ characters)");
    if (!form.preferred_repayment_date) m.push("Preferred repayment date");
    if (!form.payment_method) m.push("Payment method");
    if (form.payment_handle.trim().length < 2) m.push("Payment handle");
    if (form.payment_handle.trim() !== form.payment_handle_confirm.trim())
      m.push("Payment handle confirmation must match exactly");
    if (!form.payment_screenshot) m.push("Payment proof screenshot");
    if (!form.current_earnings_screenshot) m.push("Current earnings screenshot");
    if (!form.previous_earnings_screenshot) m.push("Previous earnings screenshot");
    if (!form.gig_name_match) m.push("Gig name match (Yes/No)");
    if (!form.payment_name_match) m.push("Payment name match (Yes/No)");
    if (nameMismatch && form.name_mismatch_explanation.trim().length < 2)
      m.push("Name mismatch explanation");
    if (!form.consent_pilot_program) m.push("Consent: pilot program");
    if (!form.consent_repay_terms) m.push("Consent: repay terms");
    if (!form.consent_no_interest) m.push("Consent: no interest");
    if (!form.consent_manual_repayment) m.push("Consent: manual repayment");
    if (!form.consent_accurate_info) m.push("Consent: accurate info");
    if (!form.consent_no_guarantee) m.push("Consent: no guarantee");
    if (!form.consent_contact) m.push("Consent: contact me");
    if (!form.consent_honest_participation) m.push("Consent: honest participation");
    if (!form.followup_survey) m.push("Followup survey willingness");
    if (!form.founder_interview) m.push("Founder interview willingness");
    if (!form.testimonial_willing) m.push("Testimonial willingness");
    if (!form.beta_testing) m.push("Beta testing willingness");
    if (!form.acquisition_source) m.push("How you heard about us");
    if (form.acquisition_source === "Friend / referral" && form.referral_handle.trim().length < 1)
      m.push("Referrer handle");
    return m;
  }, [form, age, nameMismatch]);

  const isValid = missingFields.length === 0;

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (!isValid) {
      setErrorMsg("Please complete every required field before submitting.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      const skip = new Set(["payment_screenshot","current_earnings_screenshot","previous_earnings_screenshot","additional_earnings_screenshot","platforms","use_cases"]);
      for (const [k, v] of Object.entries(form)) {
        if (skip.has(k)) continue;
        fd.append(k, v === null || v === undefined ? "" : v);
      }
      fd.append("platforms", form.platforms.join(","));
      fd.append("use_cases", form.use_cases.join(","));
      fd.append("payment_screenshot", form.payment_screenshot);
      fd.append("current_earnings_screenshot", form.current_earnings_screenshot);
      fd.append("previous_earnings_screenshot", form.previous_earnings_screenshot);
      if (form.additional_earnings_screenshot) {
        fd.append("additional_earnings_screenshot", form.additional_earnings_screenshot);
      }
      // Referral attribution from ?ref= URL param or sessionStorage
      try {
        const refCode = new URLSearchParams(window.location.search).get("ref")
          || sessionStorage.getItem("gf_ref");
        if (refCode) fd.append("referred_by", refCode);
      } catch { /* noop */ }
      const { data } = await axios.post(`${API}/pilot/apply`, fd);
      setSuccess(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Submission failed. Please try again.";
      setErrorMsg(detail);
      toast.error(detail);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#050505] text-white" data-testid="pilot-success">
        <header className="border-b border-white/5">
          <div className="max-w-[820px] mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4 text-white/60" />
              <LogoMark size={28} />
              <span className="font-display font-extrabold tracking-tight">GigAfloat</span>
            </Link>
          </div>
        </header>
        <main className="mx-auto max-w-[640px] px-6 py-20 text-center">
          <div className="relative w-24 h-24 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full bg-[#00FF88]/30 blur-2xl animate-pulse" />
            <div className="relative w-full h-full rounded-full bg-[#0A0A0A] border border-[#00FF88]/40 grid place-items-center edge-glow">
              <PartyPopper className="w-10 h-10 text-[#00FF88]" />
            </div>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mb-4">
            Thank you for applying to the GigAfloat <span className="text-[#00FF88]">Founder Pilot Program.</span>
          </h1>
          <p className="text-white/65 leading-relaxed mb-8">
            Your application has been received and will be manually reviewed. Approval is not guaranteed. If selected, you will be contacted with next steps. Please do not submit multiple applications.
          </p>
          <div className="inline-flex items-center gap-3 bg-[#00FF88]/8 border border-[#00FF88]/30 rounded-full px-5 py-2.5 mb-10">
            <span className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-[#00FF88]">// Application ID</span>
            <span className="font-mono-c text-sm text-white">{success.application_id}</span>
          </div>
          <div>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-[#00FF88] text-black font-bold text-sm px-6 py-3 rounded-full btn-glow"
              data-testid="pilot-success-home"
            >
              Back to GigAfloat <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="pilot-page">
      <header className="border-b border-white/5 sticky top-0 z-40 bg-[#050505]/70 backdrop-blur-xl">
        <div className="max-w-[820px] mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4 text-white/60" />
            <LogoMark size={28} />
            <span className="font-display font-extrabold tracking-tight">GigAfloat</span>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-[760px] px-6 lg:px-12 py-12">
        <div className="font-mono-c text-[10px] uppercase tracking-[0.25em] text-[#00FF88] mb-3">// Float Pilot Program</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mb-3">Apply for Float Pilot</h1>
        <p className="text-white/60 text-base sm:text-lg mb-6 leading-relaxed max-w-[640px]">
          A limited early-access program for select gig workers to request a small short-term float while helping GigAfloat validate demand, repayment behavior, and real-world use cases before launch.
        </p>

        <div className="rounded-2xl border border-[#00FF88]/25 bg-[#00FF88]/5 p-5 mb-8 text-sm text-white/75 leading-relaxed">
          <div className="flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-[#00FF88] mt-0.5 shrink-0" />
            <div>
              <strong className="text-white">Founder-led pilot.</strong> Manual review · No automatic approvals · No automatic withdrawals · No interest · No late fees · No credit reporting. Advance amounts <strong className="text-white">$25 or $50</strong> · Repayment due <strong className="text-white">3 calendar days for $25 · 5 calendar days for $50</strong> after funds are sent · Repay the exact amount received via the universal Square link.
            </div>
          </div>
        </div>

        {errorMsg && (
          <div className="rounded-2xl border border-red-500/40 bg-red-500/10 p-4 mb-6 flex items-start gap-3" data-testid="pilot-error">
            <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
            <div className="text-sm text-red-200">{errorMsg}</div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-10" data-testid="pilot-form">
          {/* ----- Section: Identity ----- */}
          <section>
            <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-[#00FF88] mb-1">// Section 1</div>
            <h2 className="font-display text-2xl font-extrabold tracking-tight mb-5">Tell us who you are</h2>
            <div className="grid sm:grid-cols-2 gap-x-4">
              <Field label="First name"><Input value={form.first_name} onChange={(e) => set({ first_name: e.target.value })} data-testid="p-first" /></Field>
              <Field label="Last name"><Input value={form.last_name} onChange={(e) => set({ last_name: e.target.value })} data-testid="p-last" /></Field>
            </div>
            <Field label="Email address"><Input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} data-testid="p-email" placeholder="you@example.com" /></Field>
            <Field label="Phone number"><Input type="tel" value={form.phone} onChange={(e) => set({ phone: e.target.value })} placeholder="(555) 555-0100" data-testid="p-phone" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="City"><Input value={form.city} onChange={(e) => set({ city: e.target.value })} placeholder="Atlanta" data-testid="p-city" /></Field>
              <Field label="State"><Input value={form.state} onChange={(e) => set({ state: e.target.value.toUpperCase().slice(0, 2) })} placeholder="GA" maxLength={2} data-testid="p-state" /></Field>
            </div>
            <Field label="ZIP code"><Input inputMode="numeric" value={form.zip_code} onChange={(e) => set({ zip_code: e.target.value.replace(/[^\d-]/g, "").slice(0, 10) })} placeholder="30301" data-testid="p-zip" /></Field>
            <Field
              label="Date of birth"
              hint="Applicants must be 18 or older to participate."
            >
              <Input type="date" value={form.date_of_birth} onChange={(e) => set({ date_of_birth: e.target.value })} max={todayISO()} data-testid="p-dob" />
              {form.date_of_birth && age !== null && age < 18 && (
                <span className="block mt-1.5 text-[12px] text-red-400">You must be 18+ to apply.</span>
              )}
            </Field>
          </section>

          {/* ----- Section: Gig work ----- */}
          <section>
            <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-[#00FF88] mb-1">// Section 2</div>
            <h2 className="font-display text-2xl font-extrabold tracking-tight mb-5">Your earnings</h2>

            <Field label="Are you currently doing gig work?">
              <div className="grid grid-cols-2 gap-2.5">
                {YESNO.map(([v, l]) => (
                  <OptionButton key={v} active={form.currently_gig_working === v} onClick={() => set({ currently_gig_working: v })} testid={`p-gig-${v}`}>{l}</OptionButton>
                ))}
              </div>
            </Field>

            <Field label="Gig platform(s) you use (multi-select)">
              <div className="flex flex-wrap gap-2">
                {PLATFORMS.map((p) => (
                  <Pill key={p} active={form.platforms.includes(p)} onClick={() => togglePlatform(p)} testid={`p-plat-${p.toLowerCase().replace(/\s/g,"-")}`}>{p}</Pill>
                ))}
              </div>
            </Field>

            {form.platforms.length > 0 && (
              <Field label="Primary gig platform">
                <div className="grid grid-cols-2 gap-2.5">
                  {form.platforms.map((p) => (
                    <OptionButton key={p} active={form.primary_platform === p} onClick={() => set({ primary_platform: p })} testid={`p-primary-${p.toLowerCase().replace(/\s/g,"-")}`}>{p}</OptionButton>
                  ))}
                </div>
              </Field>
            )}

            <Field label="How long have you been doing gig work?">
              <div className="grid grid-cols-2 gap-2.5">
                {GIG_EXP.map(([v, l]) => (
                  <OptionButton key={v} active={form.gig_experience === v} onClick={() => set({ gig_experience: v })} testid={`p-exp-${v}`}>{l}</OptionButton>
                ))}
              </div>
            </Field>

            <Field label="Average weekly gig earnings">
              <div className="grid grid-cols-2 gap-2.5">
                {WEEKLY_EARN.map(([v, l]) => (
                  <OptionButton key={v} active={form.weekly_earnings_range === v} onClick={() => set({ weekly_earnings_range: v })} testid={`p-weekly-${v}`}>{l}</OptionButton>
                ))}
              </div>
            </Field>

            <Field label="Average daily gig earnings">
              <div className="grid grid-cols-2 gap-2.5">
                {DAILY_EARN.map(([v, l]) => (
                  <OptionButton key={v} active={form.daily_earnings_range === v} onClick={() => set({ daily_earnings_range: v })} testid={`p-daily-${v}`}>{l}</OptionButton>
                ))}
              </div>
            </Field>

            <Field label="How many days per week do you usually work?">
              <div className="grid grid-cols-2 gap-2.5">
                {DAYS_WEEK.map(([v, l]) => (
                  <OptionButton key={v} active={form.days_per_week === v} onClick={() => set({ days_per_week: v })} testid={`p-days-${v}`}>{l}</OptionButton>
                ))}
              </div>
            </Field>

            <Field label="When is your next expected gig payout?">
              <div className="grid grid-cols-2 gap-2.5">
                {NEXT_PAYOUT.map(([v, l]) => (
                  <OptionButton key={v} active={form.next_payout === v} onClick={() => set({ next_payout: v })} testid={`p-payout-${v}`}>{l}</OptionButton>
                ))}
              </div>
            </Field>
          </section>

          {/* ----- Section: The ask ----- */}
          <section>
            <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-[#00FF88] mb-1">// Section 3</div>
            <h2 className="font-display text-2xl font-extrabold tracking-tight mb-5">The float</h2>

            <Field label="Amount requested">
              <div className="grid grid-cols-2 gap-2.5">
                {[["25","$25"],["50","$50"]].map(([v, l]) => (
                  <OptionButton key={v} active={form.amount_requested === v} onClick={() => set({ amount_requested: v, preferred_repayment_date: dueDateFromAmount(v) })} testid={`p-amount-${v}`}>{l}</OptionButton>
                ))}
              </div>
            </Field>

            <Field label="What do you need the float for? (multi-select)">
              <div className="flex flex-wrap gap-2">
                {USE_CASES.map((u) => (
                  <Pill key={u} active={form.use_cases.includes(u)} onClick={() => toggleUse(u)} testid={`p-use-${u.toLowerCase().replace(/\s/g,"-")}`}>{u}</Pill>
                ))}
              </div>
            </Field>

            <Field label="Briefly explain what the float would help with">
              <textarea
                value={form.use_explanation}
                onChange={(e) => set({ use_explanation: e.target.value.slice(0, 600) })}
                rows={3}
                placeholder="A sentence or two is plenty."
                className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-2xl text-white placeholder:text-white/30 focus:outline-none focus:border-[#00FF88]/50 focus:ring-2 focus:ring-[#00FF88]/20 transition-all resize-none text-sm"
                data-testid="p-explanation"
              />
              <div className="flex justify-end mt-1">
                <span className="text-[10px] font-mono-c text-white/35">{form.use_explanation.length}/600</span>
              </div>
            </Field>

            <Field label="Preferred repayment date" hint={`Default: ${repaymentDaysFor(form.amount_requested)} calendar days from today (3 for $25, 5 for $50). Admin may adjust after approval.`}>
              <Input type="date" value={form.preferred_repayment_date} onChange={(e) => set({ preferred_repayment_date: e.target.value })} min={todayISO()} data-testid="p-due" />
            </Field>
          </section>

          {/* ----- Section: Payment ----- */}
          <section>
            <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-[#00FF88] mb-1">// Section 4</div>
            <h2 className="font-display text-2xl font-extrabold tracking-tight mb-5">How to receive funds</h2>

            <Field label="Preferred method to receive funds">
              <div className="grid grid-cols-3 gap-2.5">
                {PAY_METHODS.map(([v, l]) => (
                  <OptionButton key={v} active={form.payment_method === v} onClick={() => set({ payment_method: v })} testid={`p-method-${v.toLowerCase().replace(/\s/g,"")}`}>{l}</OptionButton>
                ))}
              </div>
            </Field>

            <Field label="Payment handle / username / phone / email used for selected method"><Input value={form.payment_handle} onChange={(e) => set({ payment_handle: e.target.value })} placeholder="$cashtag, phone or email" data-testid="p-handle" /></Field>

            <Field label="Confirm payment handle">
              <Input value={form.payment_handle_confirm} onChange={(e) => set({ payment_handle_confirm: e.target.value })} placeholder="Re-enter exactly" data-testid="p-handle-confirm" />
              {form.payment_handle && form.payment_handle_confirm && form.payment_handle.trim() !== form.payment_handle_confirm.trim() && (
                <span className="block mt-1.5 text-[12px] text-red-400">Payment information does not match. Please verify before submitting.</span>
              )}
            </Field>

            <FileDrop
              label="Upload screenshot proving payment method belongs to you"
              file={form.payment_screenshot}
              onChange={(f) => set({ payment_screenshot: f })}
              testid="p-file-payment"
              hint="Examples: Cash App profile, Zelle screen showing your name/email/phone. Please hide or crop any sensitive information. Do not upload bank account numbers, debit/credit card numbers, full SSN, or passwords."
            />
          </section>

          {/* ----- Section: Earnings verification ----- */}
          <section>
            <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-[#00FF88] mb-1">// Section 5</div>
            <h2 className="font-display text-2xl font-extrabold tracking-tight mb-2">Earnings verification</h2>
            <p className="text-white/55 text-sm mb-5 leading-relaxed">
              Screenshots may include DoorDash, Uber, Lyft, Instacart, Spark, Roadie, or other gig platform earnings screens. <strong className="text-white/80">Do not</strong> upload screenshots showing full bank account numbers, full card numbers, Social Security numbers, passwords, or other sensitive information.
            </p>
            <FileDrop label="Current gig earnings screenshot" file={form.current_earnings_screenshot} onChange={(f) => set({ current_earnings_screenshot: f })} testid="p-file-current" />
            <FileDrop label="Previous week gig earnings screenshot" file={form.previous_earnings_screenshot} onChange={(f) => set({ previous_earnings_screenshot: f })} testid="p-file-prev" />
            <FileDrop label="Additional gig earnings screenshot" file={form.additional_earnings_screenshot} onChange={(f) => set({ additional_earnings_screenshot: f })} testid="p-file-extra" required={false} />
          </section>

          {/* ----- Section: Identity check ----- */}
          <section>
            <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-[#00FF88] mb-1">// Section 6</div>
            <h2 className="font-display text-2xl font-extrabold tracking-tight mb-5">Identity check</h2>

            <Field label="Does the name on your gig account match the name on this application?">
              <div className="grid grid-cols-2 gap-2.5">
                {YESNO.map(([v, l]) => (
                  <OptionButton key={v} active={form.gig_name_match === v} onClick={() => set({ gig_name_match: v })} testid={`p-gigname-${v}`}>{l}</OptionButton>
                ))}
              </div>
            </Field>

            <Field label="Does the name on your payment method match the name on this application?">
              <div className="grid grid-cols-2 gap-2.5">
                {YESNO.map(([v, l]) => (
                  <OptionButton key={v} active={form.payment_name_match === v} onClick={() => set({ payment_name_match: v })} testid={`p-payname-${v}`}>{l}</OptionButton>
                ))}
              </div>
            </Field>

            {nameMismatch && (
              <Field label="Please explain the name mismatch">
                <textarea
                  value={form.name_mismatch_explanation}
                  onChange={(e) => set({ name_mismatch_explanation: e.target.value.slice(0, 400) })}
                  rows={3}
                  placeholder="A short explanation helps the founder verify your account."
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-2xl text-white placeholder:text-white/30 focus:outline-none focus:border-[#00FF88]/50 focus:ring-2 focus:ring-[#00FF88]/20 transition-all resize-none text-sm"
                  data-testid="p-mismatch"
                />
              </Field>
            )}
          </section>

          {/* ----- Section: Consents ----- */}
          <section>
            <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-[#00FF88] mb-1">// Section 7</div>
            <h2 className="font-display text-2xl font-extrabold tracking-tight mb-5">Agreements</h2>

            <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-5">
              {[
                ["consent_pilot_program",      "I understand this is a limited founder-led pilot program and approval is not guaranteed."],
                ["consent_repay_terms",        "I understand that if approved and funded, I agree to repay the exact amount I receive on time — within 3 calendar days for $25 or 5 calendar days for $50 — using the universal Square repayment link GigAfloat provides."],
                ["consent_no_interest",        "I understand there is no interest, no late fee, and no automatic withdrawal during this pilot."],
                ["consent_manual_repayment",   "I understand I am responsible for manually repaying GigAfloat through the repayment method provided after approval."],
                ["consent_accurate_info",      "I certify that the information and screenshots I provided are accurate and belong to me."],
                ["consent_no_guarantee",       "I understand submitting this application does not guarantee future funding, future approvals, or access to future GigAfloat products."],
                ["consent_contact",            "I consent to GigAfloat contacting me by email, text, or phone about my application, repayment reminders, pilot feedback, and future beta testing."],
                ["consent_honest_participation", "I agree to participate honestly and understand this pilot is being used to validate demand, repayment behavior, and product-market fit."],
              ].map(([k, label]) => (
                <Checkbox key={k} checked={form[k]} onChange={(c) => set({ [k]: c })} testid={`p-${k.replace(/_/g,"-")}`}>{label}</Checkbox>
              ))}
            </div>
          </section>

          {/* ----- Section: Follow-up ----- */}
          <section>
            <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-[#00FF88] mb-1">// Section 8</div>
            <h2 className="font-display text-2xl font-extrabold tracking-tight mb-5">Help us learn</h2>

            <Field label="Willing to complete a short follow-up survey after repayment?">
              <div className="grid grid-cols-2 gap-2.5">
                {YESNO.map(([v, l]) => (
                  <OptionButton key={v} active={form.followup_survey === v} onClick={() => set({ followup_survey: v })} testid={`p-survey-${v}`}>{l}</OptionButton>
                ))}
              </div>
            </Field>

            <Field label="Willing to do a short interview with the founder?">
              <div className="grid grid-cols-2 gap-2.5">
                {YESNO.map(([v, l]) => (
                  <OptionButton key={v} active={form.founder_interview === v} onClick={() => set({ founder_interview: v })} testid={`p-interview-${v}`}>{l}</OptionButton>
                ))}
              </div>
            </Field>

            <Field label="If GigAfloat helps you, would you be open to sharing a testimonial?">
              <div className="grid grid-cols-3 gap-2.5">
                {YESNOMAYBE.map(([v, l]) => (
                  <OptionButton key={v} active={form.testimonial_willing === v} onClick={() => set({ testimonial_willing: v })} testid={`p-test-${v}`}>{l}</OptionButton>
                ))}
              </div>
            </Field>

            <Field label="Would you like to be considered for future beta testing?">
              <div className="grid grid-cols-2 gap-2.5">
                {YESNO.map(([v, l]) => (
                  <OptionButton key={v} active={form.beta_testing === v} onClick={() => set({ beta_testing: v })} testid={`p-beta-${v}`}>{l}</OptionButton>
                ))}
              </div>
            </Field>

            <Field label="How did you hear about GigAfloat?">
              <div className="grid grid-cols-2 gap-2.5">
                {ACQ_SOURCES.map((s) => (
                  <OptionButton key={s} active={form.acquisition_source === s} onClick={() => set({ acquisition_source: s })} testid={`p-source-${s.toLowerCase().replace(/[^a-z]/g,"-")}`}>{s}</OptionButton>
                ))}
              </div>
            </Field>

            <Field
              label="Referral name or handle"
              required={form.acquisition_source === "Friend / referral"}
              hint={form.acquisition_source === "Friend / referral" ? "Required — who referred you?" : "Optional unless someone referred you."}
            >
              <Input value={form.referral_handle} onChange={(e) => set({ referral_handle: e.target.value })} placeholder="@theirhandle or their name" data-testid="p-referral" />
            </Field>
          </section>

          {/* ----- Submit ----- */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={loading || !isValid}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#00FF88] text-black font-bold text-base px-8 py-4 rounded-full btn-glow disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="pilot-submit"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>Submit application <ArrowRight className="w-5 h-5" /></>
              )}
            </button>
            {!isValid && (
              <div className="mt-4 rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-4" data-testid="pilot-missing-fields">
                <div className="font-mono-c text-[10px] uppercase tracking-[0.2em] text-amber-300 mb-2">
                  // {missingFields.length} field{missingFields.length === 1 ? "" : "s"} still needed
                </div>
                <ul className="text-[13px] text-white/75 space-y-1.5 max-h-56 overflow-y-auto pr-2">
                  {missingFields.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <span className="text-amber-300 mt-1.5 w-1 h-1 rounded-full bg-amber-300 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p className="mt-4 text-[11px] text-white/35 leading-relaxed">
              Questions? <a href="mailto:gigafloatapp@gmail.com" className="text-[#00FF88] underline">gigafloatapp@gmail.com</a>
            </p>
          </div>
        </form>
      </main>
    </div>
  );
};

export default Pilot;
